import React from "react";
import { TabsContent } from "@/components/ui/tabs";
import { CardContent, Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { format, parseISO } from "date-fns";

function BookingDetailsTabs({ singleBooking = null }) {
  const booking = singleBooking;

  const getFormattedDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return format(parseISO(dateStr), "yyyy-MM-dd");
    } catch (e) {
      return dateStr.split("T")[0] || dateStr;
    }
  };

  const getFormattedBookingType = (type) => {
    if (!type) return "N/A";
    const mapping = {
      in_person: "In Person",
      phone: "Phone Call",
      phone_call: "Phone Call",
      online: "Online",
      other: "Other"
    };
    if (mapping[type]) {
      return mapping[type];
    }
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getFormattedTime = (dateStr, timeStr, dateTimeStr) => {
    if (dateTimeStr) {
      try {
        const normalized = dateTimeStr.includes("T") ? dateTimeStr : dateTimeStr.replace(" ", "T");
        return format(parseISO(normalized), "yyyy-MM-dd hh:mm a");
      } catch (e) {
        return dateTimeStr;
      }
    }
    if (dateStr && timeStr) {
      try {
        const datePart = dateStr.split("T")[0];
        const combined = `${datePart}T${timeStr}`;
        return format(parseISO(combined), "yyyy-MM-dd hh:mm a");
      } catch (e) {
        return `${dateStr.split("T")[0]} ${timeStr}`;
      }
    }
    return "N/A";
  };

  const tables = booking?.tables || (booking?.table ? [booking.table] : []);
  const staff = booking?.staff;
  const staffName = booking?.staff_name || (staff ? (staff.name || staff.full_name) : null);

  return (
    <TabsContent value="booking-details" className="p-0">
      <div className="flex flex-col mt-2 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer Info */}
          <div className="flex flex-col bg-cblack5 p-3 space-y-2 ">
            <h3 className="font-semibold text-cwhite2 text-lg px-1">
              Customer Info
            </h3>
            <Card className="bg-cblack5 border-gray-800">
              <CardContent className="pt-3">
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <span className="w-full sm:w-40 text-cwhite2 text-sm font-medium">
                      Customer Name
                    </span>
                    <span className="text-sm text-cwhite3 flex-1">
                      : {booking?.customer?.full_name || booking?.name || "N/A"}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <span className="w-full sm:w-40 text-cwhite2 text-sm font-medium">
                      Phone Number
                    </span>
                    <span className="text-sm text-cwhite3 flex-1">
                      : {booking?.customer?.number || booking?.phone || "N/A"}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <span className="w-full sm:w-40 text-cwhite2 text-sm font-medium">
                      Email
                    </span>
                    <span className="text-sm text-cwhite3 flex-1 break-all">
                      : {booking?.customer?.email || booking?.email || "N/A"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Booking Info */}
          <div className="flex flex-col space-y-2 bg-cblack5 p-3">
            <h3 className="font-semibold text-cwhite2 text-lg px-1">
              Booking Info
            </h3>
            <Card className="bg-cblack5 border-gray-800">
              <CardContent className="pt-3">
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <span className="w-full sm:w-40 text-cwhite2 text-sm font-medium">
                      Booking Type
                    </span>
                    <span className="text-sm text-cwhite3 flex-1">
                      : {getFormattedBookingType(booking?.reservation_type)}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <span className="w-full sm:w-40 text-cwhite2 text-sm font-medium">
                      Booking Date
                    </span>
                    <span className="text-sm text-cwhite3 flex-1">
                      : {getFormattedDate(booking?.reservation_date)}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <span className="w-full sm:w-40 text-cwhite2 text-sm font-medium">
                      Start Time
                    </span>
                    <span className="text-sm text-cwhite3 flex-1">
                      : {getFormattedTime(booking?.reservation_date, booking?.start_time, booking?.start_date_time)}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <span className="w-full sm:w-40 text-cwhite2 text-sm font-medium">
                      End Time
                    </span>
                    <span className="text-sm text-cwhite3 flex-1">
                      : {getFormattedTime(booking?.reservation_date, booking?.end_time, booking?.end_date_time)}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <span className="w-full sm:w-40 text-cwhite2 text-sm font-medium">
                      Number Of Guest
                    </span>
                    <span className="text-sm text-cwhite3 flex-1">
                      : {booking?.guest || "0"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Section for Tables/Staff */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          <div className="space-y-3 bg-cblack5 p-3">
            <Label className="text-cwhite text-base font-semibold px-1">
              Table Details
            </Label>
            <Card className="bg-cblack5 border-gray-800">
              <CardContent className="pt-3">
                <div className="flex flex-col gap-3">
                  {tables && tables.length > 0 ? (
                    tables.map((table, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm p-2 bg-cblack3 rounded border border-gray-700">
                        <span className="text-cwhite2">{table.name || "N/A"}</span>
                        <span className="text-cwhite3 text-xs">ID: {table.id}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-cwhite3 text-sm italic">No tables assigned</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-3 bg-cblack5 p-3">
            <Label className="text-cwhite text-base font-semibold px-1">
              Swap Table Details
            </Label>
            <Card className="bg-cblack5 border-gray-800">
              <CardContent className="pt-3">
                <div className="flex flex-col gap-3">
                  {booking?.table_swap_history && booking.table_swap_history.length > 0 ? (
                    booking.table_swap_history.map((swap, idx) => (
                      <div key={idx} className="flex flex-col gap-1 text-xs p-2 bg-cblack3 rounded border border-gray-700">
                        <div className="flex justify-between text-sm text-cwhite2 font-semibold">
                          <span>Table Swapped</span>
                          <span className="text-cyellow">
                            {swap.old_table_name || `T${swap.old_table_id}`} → {swap.new_table_name || `T${swap.new_table_id}`}
                          </span>
                        </div>
                        <div className="flex justify-between text-cwhite3 mt-1">
                          <span>By User: {swap.swapped_by}</span>
                          <span>{swap.swapped_at}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-cwhite3 text-sm italic">No swap history available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-3 bg-cblack5 p-3">
            <Label className="text-cwhite text-base font-semibold px-1">
              Staff Details
            </Label>
            <Card className="bg-cblack5 border-gray-800">
              <CardContent className="pt-3">
                <div className="flex flex-col gap-3">
                  {staffName ? (
                    <div className="flex justify-between items-center text-sm p-2 bg-cblack3 rounded border border-gray-700">
                      <span className="text-cwhite2">{staffName}</span>
                      {staff?.id && <span className="text-cwhite3 text-xs">ID: {staff.id}</span>}
                    </div>
                  ) : (
                    <p className="text-cwhite3 text-sm italic">No staff details available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </TabsContent>
  );
}

export default BookingDetailsTabs;
